// Implements: F-IDN-027
/**
 * The organization a P3 page works on. It offers the organizations whose scopes carry one of the
 * page's actions (`ScopeEntry.permissions`, never a role), first by name; with more than one, a
 * picker chooses. The page is mounted keyed by the chosen organization's id (checklist 14), so
 * switching organization mounts a fresh page: no state or late answer of the previous one shows.
 */

import { Fragment, useId, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Select } from "../../../shared/ui/atoms/Select";
import { Field } from "../../../shared/ui/molecules/Field";
import type { BackofficeScope, ScopePermission } from "../../../types/api";
import styles from "../components/Organization.module.css";
import { organizationTargets } from "../model/targets";
import type { OrganizationTarget } from "../model/targets";

export interface ByOrganizationProps {
  readonly scopes: readonly BackofficeScope[];
  readonly actions: readonly ScopePermission[];
  readonly children: (target: OrganizationTarget) => ReactNode;
}

export function ByOrganization({ scopes, actions, children }: ByOrganizationProps): ReactElement | null {
  const { t } = useTranslation();
  const pickerId = useId();
  const targets = organizationTargets(scopes, actions);
  const [chosen, setChosen] = useState<string | null>(null);
  const target = targets.find((candidate) => candidate.organizationId === chosen) ?? targets[0];
  if (target === undefined) return null;

  return (
    <div className={styles.page}>
      {targets.length > 1 ? (
        <div className={styles.toolbar}>
          <Field id={pickerId} label={t("organization.target.label")}>
            <Select
              id={pickerId}
              value={target.organizationId}
              options={targets.map((candidate) => ({
                value: candidate.organizationId,
                label: candidate.name ?? t("organization.target.label"),
              }))}
              onChange={setChosen}
            />
          </Field>
        </div>
      ) : null}
      <Fragment key={target.organizationId}>{children(target)}</Fragment>
    </div>
  );
}
